import { Trash2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { ToggleGroup, View, XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import type { SortableItemWithId } from "../../../core/components/SortableList";
import { ToggleGroupItem } from "../../../core/components/ToggleGroupItem";
import { useStores } from "../../../core/stores";
import { Permission } from "../../../permissions/types";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { Title } from "../../../ui/Title";
import { BusinessModuleBlockRenderer } from "../../modules/BusinessModuleUIRenderer";
import { BusinessModuleType } from "../../modules/types";
import { BusinessAdminTitleAndEmojiEdit } from "../../modules/ui/BusinessAdminEmojiPicker";
import type {
  BusinessModuleTaskInfo,
  BusinessPlanModel,
  BusinessPlanSection,
} from "../BusinessPlanModel";
import { BusinessSortableList } from "../ui/BusinessSortableList";

type BusinessPlanSectionUIProps = {
  businessPlanUid: string;
  section: BusinessPlanSection;
};

type BusinessModuleTaskInfoWithUid = BusinessModuleTaskInfo & { uid: string };

const SectionModuleListAdmin = observer(
  ({
    section,
    items,
    renderItem,
    businessPlan,
  }: {
    section: BusinessPlanSection;
    items: BusinessModuleTaskInfoWithUid[];
    renderItem: (
      section: BusinessModuleTaskInfoWithUid,
    ) => React.JSX.Element | null;
    businessPlan: BusinessPlanModel;
  }) => {
    const renderItemWithSortable = React.useCallback(
      (item: SortableItemWithId<BusinessModuleTaskInfoWithUid>) => {
        const businessTaskInfo = item.item;

        return (
          <XStack width="100%" alignItems={"center"}>
            {renderItem(businessTaskInfo)}

            <View position={"absolute"} right={0}>
              <PopoverConfirm
                placement="top-start"
                onConfirm={() => {
                  section.items = section.items.filter(
                    (i) =>
                      i.businessModuleUid !==
                      businessTaskInfo.businessModuleUid,
                  );
                  businessPlan.updateSection(section);
                }}
              >
                <Trash2 />
              </PopoverConfirm>
            </View>
          </XStack>
        );
      },
      [renderItem, businessPlan, section],
    );

    const onSort = React.useCallback(
      (items: BusinessModuleTaskInfoWithUid[]) => {
        section.items = items;

        businessPlan.updateSection(section);
      },
      [businessPlan, section],
    );

    const onAdd = React.useCallback(
      async (infoTitle: string) => {
        const infoModule = await businessPlan.addBusinessModule({
          type: BusinessModuleType.Info,
          title: infoTitle,
          emoji: null,
          descriptionYooptaEditor: null,
        });

        await businessPlan.addSectionModule(section.uid, infoModule.uid);
      },
      [section, businessPlan],
    );

    return (
      <>
        <YStack gap="$medium">
          <BusinessAdminTitleAndEmojiEdit
            formValues={section}
            onChange={(f) =>
              businessPlan.updateSection({
                ...section,
                title: f.title ?? "",
                emoji: f.emoji ?? null,
              })
            }
          />

          <Label>Visible in menu</Label>
          <ToggleGroup
            borderRadius={0}
            backgroundColor={"transparent"}
            type={"single"}
            disableDeactivation
            value={section.isVisible ? "visible" : "not-visible"}
            onValueChange={(value) => {
              businessPlan.updateSection({
                ...section,
                isVisible: value === "visible",
                isDeleted: false,
              });
            }}
          >
            <ToggleGroupItem active={section.isVisible} value="visible">
              <Body>Visible</Body>
            </ToggleGroupItem>
            <ToggleGroupItem active={!section.isVisible} value="not-visible">
              <Body>Not visible</Body>
            </ToggleGroupItem>
          </ToggleGroup>
        </YStack>

        <BusinessSortableList<BusinessModuleTaskInfoWithUid>
          itemDescription={"Item"}
          items={items}
          renderItem={renderItemWithSortable}
          onSort={onSort}
          onAdd={onAdd}
        />
      </>
    );
  },
);

export const BusinessPlanSectionUI = observer(
  ({ businessPlanUid, section }: BusinessPlanSectionUIProps) => {
    const { businessModulesApi } = useApi();
    const { businessPlansStore } = useStores();

    const navigation = usePlanNavigation();

    // Load all modules
    React.useEffect(() => {
      const unsubs: (() => void)[] = section.items.map(
        ({ businessModuleUid }) =>
          businessModulesApi.watchItem(businessModuleUid),
      );

      return () => {
        unsubs.forEach((unsub) => {
          unsub();
        });
      };
    }, [section, businessModulesApi]);

    const renderItem = React.useCallback(
      ({ isVisible, businessModuleUid }: BusinessModuleTaskInfoWithUid) => {
        if (!isVisible) {
          return null;
        }

        return (
          <BusinessModuleBlockRenderer
            key={businessModuleUid}
            uid={businessModuleUid}
            active={true}
            upcoming={false}
            onPress={() => {
              navigation.navigate("BusinessSectionModule", {
                businessUid: businessPlanUid,
                itemUid: businessModuleUid,
              });
            }}
          />
        );
      },
      [navigation, businessPlanUid],
    );

    const businessPlan = businessPlansStore.byId.get(businessPlanUid);

    if (!businessPlan) {
      return null;
    }

    const isAdmin = businessPlan.checkPermission(Permission.CanEditSections);

    const items: BusinessModuleTaskInfoWithUid[] = section.items.map(
      (item) => ({
        uid: item.businessModuleUid,
        ...item,
      }),
    );

    return (
      <Box>
        <YStack gap={"$medium"}>
          <Title>{section.title}</Title>

          {!isAdmin && <Group>{items.map(renderItem)}</Group>}
          {isAdmin && (
            <SectionModuleListAdmin
              section={section}
              items={items}
              renderItem={renderItem}
              businessPlan={businessPlan}
            />
          )}
        </YStack>
      </Box>
    );
  },
);
